/**
 * 客户到期预警（订阅）共用的常量与计算
 *
 * 口径与 web 端 views/h5/project/sys/SubscriptionList.vue 里内联的实现一致
 * （那份是页面内自定义的，暂未抽出来；改动任一处都要两边同步）。
 *
 * 约定：
 *   publish_type / renew_type 是**类型字段**（不是状态字段），取值 1/2，后端见 SubscriptionConstant
 *   到期档：已过期(<今天) / 紧急(≤7天) / 预警(≤30天) / 正常(>30天或无到期日)
 */

/** 产品线（页面原型固定，系统字典直存字符串） */
export const PRODUCT_LINE_LIST = ["财贸", "通用", "天通", "用友", "金蝶", "生态", "行业", "快马", "物联通", "CRM", "熊掌柜", "网上管家婆"];
/** 购买方式 */
export const PURCHASE_TYPE_LIST = ["年费", "买断"];
/** 部署云 */
export const DEPLOY_CLOUD_LIST = ["阿里云", "腾讯云", "华为云", "客户自建云", "客户本地部署", "管家婆云"];

/** 部署方式：1云服务器部署 2本地部署 */
export const PUBLISH_TYPE = {CLOUD: 1, LOCAL: 2};
/** 续费方式：1服务续费（顺延服务到期） 2云服务器续费（顺延云服务器到期） */
export const RENEW_TYPE = {SERVICE: 1, CLOUD: 2};

/** 产品线选项（筛选与表单共用） */
export const productLineOptions = PRODUCT_LINE_LIST.map(line => ({label: line, value: line}));
/** 购买方式选项 */
export const purchaseTypeOptions = PURCHASE_TYPE_LIST.map(type => ({label: type, value: type}));
/** 部署云选项 */
export const deployCloudOptions = DEPLOY_CLOUD_LIST.map(cloud => ({label: cloud, value: cloud}));
/** 部署方式选项 */
export const publishTypeOptions = [
    {label: "云服务器部署", value: PUBLISH_TYPE.CLOUD},
    {label: "本地部署", value: PUBLISH_TYPE.LOCAL},
];

/** 到期状态筛选项：value 与后端 SubscriptionQueryVo.status 的判定文案对应，空串=全部 */
export const STATUS_OPTIONS = [
    {label: "已过期", value: "已过期"},
    {label: "紧急（≤7天）", value: "紧急"},
    {label: "预警（≤30天）", value: "预警"},
    {label: "正常（>30天）", value: "正常"},
];

/** 部署方式是否云服务器部署（select 的值可能是数字或字符串，统一按数字比） */
export function isCloudPublish(value) {
    return Number(value) === PUBLISH_TYPE.CLOUD;
}

export function publishTypeText(value) {
    if (isCloudPublish(value)) {
        return "云服务器部署";
    }
    return Number(value) === PUBLISH_TYPE.LOCAL ? "本地部署" : "—";
}

/** 续费方式文案；存量数据 renew_type 可能为空，按服务续费对待（与后端 renew 一致） */
export function renewTypeText(value) {
    const n = Number(value);
    if (n === RENEW_TYPE.CLOUD) {
        return "云服务器续费";
    }
    return (value === null || value === undefined || value === "" || n === RENEW_TYPE.SERVICE) ? "服务续费" : "—";
}

/** 距到期还有几天，取不到返回 null */
export function daysToExpire(dateStr) {
    if (!dateStr) {
        return null;
    }
    const parts = String(dateStr).slice(0, 10).split("-");
    if (parts.length !== 3) {
        return null;
    }
    const today = new Date();
    const expire = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return Math.round((expire - new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000);
}

/** 到期档文案：已过期 / 紧急 / 预警 / 正常 */
export function statusOf(dateStr) {
    const days = daysToExpire(dateStr);
    if (days === null) {
        return "正常";
    }
    if (days < 0) {
        return "已过期";
    }
    if (days <= 7) {
        return "紧急";
    }
    if (days <= 30) {
        return "预警";
    }
    return "正常";
}

/** 到期档 → 标签色（对应客户标签的 tag-xxx 类名后缀） */
export function statusTag(dateStr) {
    const map = {"已过期": "danger", "紧急": "warning", "预警": "primary", "正常": "success"};
    return map[statusOf(dateStr)] || "info";
}

/** 剩余文案：还有 N 天 / 今天到期 / 已过 N 天 */
export function remainText(dateStr) {
    const days = daysToExpire(dateStr);
    if (days === null) {
        return "—";
    }
    if (days < 0) {
        return "已过 " + (-days) + " 天";
    }
    if (days === 0) {
        return "今天到期";
    }
    return "还有 " + days + " 天";
}

export function remainColor(dateStr) {
    const days = daysToExpire(dateStr);
    if (days === null) {
        return "";
    }
    if (days < 0) {
        return "#c0392b";
    }
    if (days <= 7) {
        return "#e67e22";
    }
    if (days <= 30) {
        return "#2980b9";
    }
    return "#2e8b57";
}

export function dateOnly(value) {
    return value ? String(value).slice(0, 10) : "—";
}

export function dateTime(value) {
    return value ? String(value).slice(0, 19) : "—";
}

/**
 * 到期日顺延 N 个月（保日；2/29 顺延到非闰年退 2/28）
 * 与后端 SubscriptionServiceImpl#addMonthsKeepDay 口径一致，改动需两边同步
 */
export function addMonths(baseDate, years, months) {
    if (!baseDate) {
        return "";
    }
    const parts = String(baseDate).split("-");
    if (parts.length !== 3) {
        return "";
    }
    const total = (Number(years) || 0) * 12 + (Number(months) || 0);
    if (total <= 0) {
        return "";
    }
    let y = Number(parts[0]);
    let m = Number(parts[1]) + total;
    const d = Number(parts[2]);
    y = y + Math.floor((m - 1) / 12);
    m = ((m - 1) % 12) + 1;
    const lastDay = new Date(y, m, 0).getDate();
    return y + "-" + String(m).padStart(2, "0") + "-" + String(Math.min(d, lastDay)).padStart(2, "0");
}
